import { UIElement } from "../../common_ui_elements/ui_element.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { MyTextAlign } from "../../common_ui_elements/ui_text_element.js";
import { MyTextVerticalAlign } from "../../common_ui_elements/ui_text_element.js";
import { DEFAULT_LEADERBOARD_SCREEN_PLAYER_INFO_CONFIG, LeaderboardScreenPlayerInfoConfig } from "./leaderboard_screen_player_info_config.js";
import { UIAvatarSelectionItem } from "../../common_ui_elements/ui_avatar_selection_item.js";
import { UICountryFlag } from "../../common_ui_elements/ui_country_flag.js";
import { getFlagByCountryCode } from "../../../common/flag_utils.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";

const ROW_CONTENT_Y_OFFSET_DESIGN = -5;
const EYE_W_DESIGN = 65;
const EYE_H_DESIGN = 39;
const PLAYER_EYE_W_DESIGN = 65;
const PLAYER_EYE_H_DESIGN = 39;

// Profile dialog is intentionally not ported yet — row taps are a no-op for now.

interface LeaderboardPlayerVisualState
{
  row_id: ImgID;
  show_circle: boolean;
  circle_id: ImgID;
  show_star: boolean;
  star_id: ImgID;
}

function formatScore( score: number ): string
{
  return score.toString().replace( /\B(?=(\d{3})+(?!\d))/g, ' ' );
}

export class LeaderboardScreenPlayerInfo extends UIElement
{
  private config: LeaderboardScreenPlayerInfoConfig;
  private render_clip: { x: number; y: number; w: number; h: number } | null = null;
  private background_overflow: number;

  private background!: UINineSliceElement;
  private rating_star!: UIImage;
  private rating_text!: UITextElement;
  private avatar!: UIAvatarSelectionItem;
  private flag!: UICountryFlag;
  private player_name!: UITextElement;
  private player_score!: UITextElement;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    config: LeaderboardScreenPlayerInfoConfig = DEFAULT_LEADERBOARD_SCREEN_PLAYER_INFO_CONFIG,
    /** Extra width of the background on each side, beyond the row (the pinned player card). */
    background_overflow = 0,
  )
  {
    super( ctx, x, y, width, height, false, false );
    this.config = config;
    this.background_overflow = background_overflow;
    this.createElements();
  }

  public getConfig(): LeaderboardScreenPlayerInfoConfig
  {
    return this.config;
  }

  /**
   * Shows another player in this row without rebuilding it: the leaderboard list keeps a
   * screenful of rows and hands them over to the players scrolling into view.
   */
  public setConfig( config: LeaderboardScreenPlayerInfoConfig ): void
  {
    if ( config === this.config )
      return;

    // The player's own row has another substrate, stats colour and shadow.
    const prev = this.config;
    const rebuild = config.is_player !== prev.is_player;
    this.config = config;
    if ( rebuild )
    {
      this.removeAllChildren();
      this.createElements();
      this.invalidateSubtree();
      return;
    }

    // Only the parts that differ are touched and redrawn: a list scrolled fast hands rows over
    // to new players several times a frame, while the frame, score box and icons stay the same
    // (the substrate depends on `is_player` alone).
    if ( config.rating !== prev.rating )
    {
      const visual_state = this.resolveVisualState();
      this.rating_star.setImageId( visual_state.star_id );
      this.rating_star.setOpacityImmediate( visual_state.show_star ? 1 : 0 );
      this.rating_star.forceRedraw();
      this.rating_text.setTextWithoutRepaint( `${config.rating}` );
      this.rating_text.forceRedraw();
    }
    if ( config.avatar_id !== prev.avatar_id || ( config.facebook_av_url ?? null ) !== ( prev.facebook_av_url ?? null ) )
      this.avatar.setAvatarId( config.avatar_id, config.facebook_av_url ?? null );
    if ( config.country_code !== prev.country_code )
      this.flag.setImageFlagById( getFlagByCountryCode( config.country_code ) );
    if ( config.name !== prev.name )
    {
      this.player_name.setTextWithoutRepaint( `${config.name}` );
      this.player_name.forceRedraw();
    }
    if ( config.score !== prev.score )
    {
      this.player_score.setTextWithoutRepaint( formatScore( config.score ) );
      this.player_score.forceRedraw();
    }
    // The row itself renders on the next render(), and in it only the children marked above.
    this.forceRedraw();
  }

  /**
   * Hides rank, top-3 badge, flag, name and score. A shared backdrop also hides the avatar,
   * so each pooled row can draw its own avatar over the same image.
   */
  public hidePlayerParts( hide_avatar = false ): void
  {
    for ( const el of [ this.rating_star, this.rating_text, this.flag, this.player_name, this.player_score ] )
      el.setOpacityImmediate( 0 );
    if ( hide_avatar )
      this.avatar.setOpacityImmediate( 0 );
  }

  private resolveVisualState(): LeaderboardPlayerVisualState
  {
    const row_id = this.config.is_player
      ? ImgID.UI_LeaderboardSubstrateYouNew
      : ImgID.UI_LeaderboardSubstratePlayerNew;

    const default_circle = ImgID.UI_LeaderboardPlayerAvatarSub;

    if ( this.config.rating === 1 )
      return { row_id, show_circle: false, circle_id: default_circle, show_star: true, star_id: ImgID.UI_LeaderboardStarOrange };

    if ( this.config.rating === 2 )
      return { row_id, show_circle: false, circle_id: default_circle, show_star: true, star_id: ImgID.UI_LeaderboardStarBlue };

    if ( this.config.rating === 3 )
      return { row_id, show_circle: false, circle_id: default_circle, show_star: true, star_id: ImgID.UI_LeaderboardStarBrown };

    return { row_id, show_circle: false, circle_id: default_circle, show_star: false, star_id: ImgID.UI_LeaderboardStarBlue };
  }

  private createElements(): void
  {
    this.createBackground();
    this.createPlayerRating();
    this.createPlayerAvatar();
    this.createPlayerFlag();
    this.createPlayerName();
    this.createStatsBackground();
    this.createPlayerScore();
    this.createScoreStar();
    this.createPointsIcon();
  }

  private createPlayerFlag(): void
  {
    const design_flag_size = 55;

    const flag_w = LayoutScaler.widthFromDesign( design_flag_size );
    const flag_h = LayoutScaler.heightFromDesign( design_flag_size );
    const flag_x = LayoutScaler.xFromDesign( 166.37 );
    const flag_y = LayoutScaler.yFromDesign( 83.5 );

    const flag = new UICountryFlag(
      this.ctx,
      flag_x,
      flag_y,
      flag_w,
      flag_h,
      getFlagByCountryCode( this.config.country_code ),
    );
    flag.setClickable( false );
    this.addChild( flag );
    this.flag = flag;
  }

  private createPlayerAvatar(): void
  {
    const design_avatar_size  = 118;
    const design_border_w     = 4;

    const avatar_w = LayoutScaler.widthFromDesign( design_avatar_size );
    const avatar_h = LayoutScaler.heightFromDesign( design_avatar_size );
    const avatar_x = LayoutScaler.xFromDesign( 186 );
    const avatar_y = LayoutScaler.yFromDesign( 16 );

    const avatar = new UIAvatarSelectionItem(
      this.ctx,
      avatar_x,
      avatar_y,
      avatar_w,
      avatar_h,
      this.config.avatar_id,
      () => {},
      LayoutScaler.widthFromDesign( design_border_w ),
      0,
      undefined,
      true,
      {
        type: "linear-gradient",
        from: "#F9F4C9",
        to: "#B5A084",
        direction: "vertical"
      },
      this.config.facebook_av_url
    );
    avatar.setClickable( false );
    this.addChild( avatar );
    this.avatar = avatar;
  }

  private createBackground(): void
  {
    const visual_state = this.resolveVisualState();
    const background_w = this.rect.getW() + this.background_overflow * 2;
    const background_h = this.rect.getH();
    const background_x = -this.background_overflow;
    const background_y = 0;

    const background = new UINineSliceElement(
      this.ctx,
      background_x,
      background_y,
      background_w,
      background_h,
      visual_state.row_id,
      "leaderboard_substrate_new"
    );
    // The middle of the row art is a single stretched column: without a backing its seams
    // show the row underneath (beige under the pinned green card) as thin vertical lines.
    background.setSeamBacking( true );

    this.addChild( background );
    this.background = background;
  }

  private getStatsRect(): { x: number; y: number; w: number; h: number }
  {
    const background_margin = LayoutScaler.xFromDesign( 42 );
    const background_w = this.rect.getW() - background_margin * 2;
    const background_x = background_margin;
    const background_right = background_x + background_w;
    const eye_w = LayoutScaler.widthFromDesign(
      this.config.is_player ? PLAYER_EYE_W_DESIGN : EYE_W_DESIGN
    );
    const right_pad = LayoutScaler.widthFromDesign( 25 );
    const gap = LayoutScaler.widthFromDesign( 16 );
    const w = LayoutScaler.widthFromDesign( 260 );
    const h = LayoutScaler.heightFromDesign( 110 );
    const eye_x = background_right - right_pad - eye_w;

    return {
      x: eye_x - gap - w,
      y: ( this.rect.getH() - h ) / 2 - LayoutScaler.yFromDesign( 3 ),
      w,
      h,
    };
  }

  private createStatsBackground(): void
  {
    const stats_rect = this.getStatsRect();
    const stats_background = new RoundedRectangle(
      this.ctx,
      stats_rect.x,
      stats_rect.y,
      stats_rect.w,
      stats_rect.h,
      LayoutScaler.cornerRadiusFromDesign( 22 ),
      this.config.is_player ? "#0AB10A" : "#EBD2B7",
      undefined,
      false,
      undefined,
      0,
      undefined,
      this.config.is_player
        ? {
            color: "rgba(0, 72, 0, 0.65)",
            blur: LayoutScaler.widthFromDesign( 12 ),
            offsetX: 0,
            offsetY: LayoutScaler.yFromDesign( 6 ),
            sides: [ true, true, true, true ],
          }
        : {
            color: "rgba(126, 86, 52, 0.38)",
            blur: LayoutScaler.widthFromDesign( 12 ),
            offsetX: 0,
            offsetY: LayoutScaler.yFromDesign( 6 ),
            sides: [ true, true, true, true ],
          }
    );
    stats_background.setClickable( false );
    this.addChild( stats_background );
  }

  private createPlayerRating(): void
  {
    const design_rating_size        = 130;
    const design_rating_star_w      = 130;
    const design_rating_star_h      = 129;
    const design_rating_text_size   = 45;

    const visual_state = this.resolveVisualState();

    const rating_w = LayoutScaler.widthFromDesign( design_rating_size );
    const rating_h = LayoutScaler.heightFromDesign( design_rating_size );
    const rating_x = LayoutScaler.xFromDesign( 32 );
    const rating_y = LayoutScaler.yFromDesign( 9 );
    const rating_bg_y = rating_y;
    const rating_star_group_y = rating_y;

    const rating_star_w = LayoutScaler.widthFromDesign( design_rating_star_w );
    const rating_star_h = LayoutScaler.heightFromDesign( design_rating_star_h );
    const rating_star_x = rating_x + ( rating_w - rating_star_w ) / 2;
    const rating_star_y = rating_star_group_y + ( rating_h - rating_star_h ) / 2;

    const rating_star_bg = new UIImage(
      this.ctx,
      rating_star_x,
      rating_star_y,
      rating_star_w,
      rating_star_h,
      visual_state.star_id
    );
    rating_star_bg.setClickable( false );

    // Always there, hidden below the top 3: a recycled row can move between ranks.
    rating_star_bg.setOpacityImmediate( visual_state.show_star ? 1 : 0 );
    this.addChild( rating_star_bg );
    this.rating_star = rating_star_bg;

    const rating_text_x = rating_x;
    const rating_text_y = (
      visual_state.show_star
        ? rating_star_group_y
        : rating_bg_y
    ) + LayoutScaler.yFromDesign( 3 );
    const rating_text_w = rating_w;
    const rating_text_h = rating_h;

    const rating_text = new UITextElement(
      this.ctx,
      rating_text_x,
      rating_text_y,
      rating_text_w,
      rating_text_h,
      {
        text: `${this.config.rating}`,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_rating_text_size ),
        font_weight: 900,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#05083B",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );
    this.addChild( rating_text );
    this.rating_text = rating_text;
  }

  private createPlayerName(): void
  {
    const design_player_name_h        = 60;

    const player_name_h = LayoutScaler.heightFromDesign( design_player_name_h );
    const player_name_x = LayoutScaler.xFromDesign( 322 );
    // Name ends before the stats box; long names shrink instead of running under it.
    const player_name_w = this.getStatsRect().x - LayoutScaler.widthFromDesign( 16 ) - player_name_x;
    const rating_text_center_y = LayoutScaler.yFromDesign( 12 + 130 / 2 );
    const player_name_y = rating_text_center_y - player_name_h / 2;

    const player_name = new UITextElement(
      this.ctx,
      player_name_x,
      player_name_y,
      player_name_w,
      player_name_h,
      {
        text: `${this.config.name}`,
        text_color: this.config.is_player ? "#04113F" : "#07195B",
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 28 ),
        font_weight: 900,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );
    this.addChild( player_name );
    this.player_name = player_name;
  }

  private createPlayerScore(): void
  {
    const stats_rect = this.getStatsRect();
    const player_score_w = stats_rect.w;
    const player_score_h = LayoutScaler.heightFromDesign( 58 );
    const player_score_x = stats_rect.x;
    const player_score_y = stats_rect.y + LayoutScaler.yFromDesign( 48 );

    const player_score = new UITextElement(
      this.ctx,
      player_score_x,
      player_score_y,
      player_score_w,
      player_score_h,
      {
        text: formatScore( this.config.score ),
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        font_weight: 900,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 11 ),
        outline_color: "#65091C",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      }
    );
    this.addChild( player_score );
    this.player_score = player_score;
  }

  private createScoreStar(): void
  {
    const stats_rect = this.getStatsRect();
    const star_size = LayoutScaler.widthFromDesign( 50 );
    const star_x = stats_rect.x + ( stats_rect.w - star_size ) / 2;
    const star_y = stats_rect.y + LayoutScaler.yFromDesign( 3 );

    const star = new UIImage(
      this.ctx,
      star_x,
      star_y,
      star_size,
      star_size,
      ImgID.ProfileStarIcon
    );
    star.setClickable( false );
    this.addChild( star );
  }

  private createPointsIcon(): void
  {
    const design_points_icon_w = EYE_W_DESIGN;
    const design_points_icon_h = EYE_H_DESIGN;
    const background_margin = LayoutScaler.xFromDesign( 42 );
    const background_w = this.rect.getW() - background_margin * 2;
    const background_x = background_margin;
    const background_right = background_x + background_w;
    const right_pad = LayoutScaler.widthFromDesign( 25 );

    const points_icon_w = LayoutScaler.widthFromDesign( design_points_icon_w );
    const points_icon_h = LayoutScaler.heightFromDesign( design_points_icon_h );
    const points_icon_x = background_right - right_pad - points_icon_w;
    const points_icon_y = ( this.rect.getH() - points_icon_h ) / 2
      + LayoutScaler.yFromDesign( ROW_CONTENT_Y_OFFSET_DESIGN );

    const points_icon = new UIImage(
      this.ctx,
      points_icon_x,
      points_icon_y,
      points_icon_w,
      points_icon_h,
      ImgID.UI_LeaderboardEye,
    );
    points_icon.setClickable( false );
    this.addChild( points_icon );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    if ( this.render_clip )
    {
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.rect( this.render_clip.x, this.render_clip.y, this.render_clip.w, this.render_clip.h );
      this.ctx.clip();
    }

    this.startRender();
    this.finishRender();

    if ( this.render_clip )
      this.ctx.restore();
  }

  protected override syncDomContent( node: HTMLElement ): boolean
  {
    let clip = "";
    if ( this.render_clip )
    {
      const pos = this.pixelPosForRender();
      const w = this.rect.getW();
      const h = this.rect.getH();
      const c = this.render_clip;
      const top = Math.max( 0, c.y - pos.y );
      const left = Math.max( 0, c.x - pos.x );
      const bottom = Math.max( 0, pos.y + h - ( c.y + c.h ) );
      const right = Math.max( 0, pos.x + w - ( c.x + c.w ) );
      clip = `inset(${top}px ${right}px ${bottom}px ${left}px)`;
    }
    if ( node.style.clipPath !== clip )
      node.style.clipPath = clip;
    return true;
  }

  public setRenderClip( x: number, y: number, w: number, h: number ): void
  {
    this.render_clip = { x, y, w, h };
  }

  public clearRenderClip(): void
  {
    this.render_clip = null;
  }

  public name(): string
  {
    return "LeaderboardScreenPlayerSub";
  }
}
